import { useCallback, useEffect, useRef, useState } from "react";
import { fmtMYT, fmtMYTDate } from "@/lib/myt";
import { ImagePlus, LineChart, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const EMPTY_FORM = { name: "", description: "", full_description: "", image_url: "", tradingview_ref: "", tutorial_link: "", status: "active", access_type: "free", eligible_plans: [], coin_price: "", coin_duration_days: "" };
const REQUEST_FILTERS = [
  ["all", "All"],
  ["pending", "Pending"],
  ["active", "Active"],
  ["expired", "Expired"],
  ["renewal_pending", "Renewal Pending"],
  ["rejected", "Rejected"],
  ["auto", "Auto Approved"],
];
const STATUS_STYLES = {
  pending_admin: "text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30",
  ib_review: "text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30",
  ib_approved: "text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30",
  renewal_pending: "text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30",
  approved: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
  rejected: "text-red-400 bg-red-400/10 border-red-400/30",
  ib_rejected: "text-red-400 bg-red-400/10 border-red-400/30",
};
const STATUS_LABELS = {
  pending_admin: "Pending Admin Approval",
  ib_review: "Pending IB Review",
  ib_approved: "IB Approved — Admin Review",
  renewal_pending: "Pending Renewal Approval",
  approved: "Approved",
  rejected: "Rejected",
  ib_rejected: "IB Rejected",
};

function matchesFilter(r, filter) {
  if (filter === "all") return true;
  if (filter === "pending") return ["pending_admin", "ib_review", "ib_approved"].includes(r.status);
  if (filter === "active") return r.status === "approved" && r.access_status === "active";
  if (filter === "expired") return r.status === "approved" && r.access_status === "expired";
  if (filter === "renewal_pending") return r.status === "renewal_pending";
  if (filter === "rejected") return ["rejected", "ib_rejected"].includes(r.status);
  if (filter === "auto") return r.approval_mode === "auto";
  return true;
}

function AccessConfig({ value, onChange, prefix }) {
  return (
    <div className="border border-[#222530] rounded-lg p-4 space-y-3" data-testid={`${prefix}-access-config`}>
      <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B]">Access Configuration</p>
      <div className="flex gap-2">
        {[["free", "FREE"], ["coin", "COINS"]].map(([v, label]) => (
          <button
            key={v}
            type="button"
            onClick={() => onChange((f) => ({ ...f, access_type: v }))}
            className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-3 py-1.5 border transition-colors ${value.access_type === v ? "text-black bg-[#D4AF37] border-[#D4AF37]" : "text-[#D4AF37] border-[#D4AF37]/30 hover:bg-[#D4AF37]/10"}`}
            data-testid={`${prefix}-access-${v}`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="border-t border-[#222530] pt-3 space-y-3">
        <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B]">
          {value.access_type === "coin" ? "Coins Purchase (required — 1 COINS = USD 1)" : "Coins Purchase (optional — 1 COINS = USD 1)"}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] font-medium text-[#94A3B8] mb-1 tracking-wide">Price (COINS)</label>
            <input type="number" min="0" step="0.01" className="input-lux !py-2 text-xs font-mono" value={value.coin_price ?? ""} onChange={(e) => onChange((f) => ({ ...f, coin_price: e.target.value }))} placeholder="e.g. 100" data-testid={`${prefix}-coin-price-input`} />
          </div>
          <div>
            <label className="block text-[10px] font-medium text-[#94A3B8] mb-1 tracking-wide">Duration (days — empty = permanent)</label>
            <input type="number" min="1" step="1" className="input-lux !py-2 text-xs font-mono" value={value.coin_duration_days ?? ""} onChange={(e) => onChange((f) => ({ ...f, coin_duration_days: e.target.value }))} placeholder="Permanent" data-testid={`${prefix}-coin-duration-input`} />
          </div>
        </div>
        <p className="text-[10px] text-[#64748B]">Expiry-based access locks after the duration and requires repurchase. No auto-renewal.</p>
      </div>
    </div>
  );
}

const coinPayload = (v) => ({
  coin_price_minor: v.coin_price !== "" && v.coin_price != null ? Math.round(Number(v.coin_price) * 100) : (v.coin_price_minor ?? null),
  coin_duration_days: v.coin_duration_days !== "" && v.coin_duration_days != null ? parseInt(v.coin_duration_days, 10) : (v.coin_duration_days ?? null),
});

export default function AdminIndicators() {
  const [indicators, setIndicators] = useState(null);
  const [requests, setRequests] = useState(null);
  const [accessList, setAccessList] = useState(null);
  const [filter, setFilter] = useState("all");
  const [accessSearch, setAccessSearch] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [editInd, setEditInd] = useState(null);
  const [editError, setEditError] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  const [approveModal, setApproveModal] = useState(null); // {request, start_date, expiry_date, remark}
  const [rejectModal, setRejectModal] = useState(null); // {request, reason}
  const [historyModal, setHistoryModal] = useState(null); // {member_id, member_name, data|null}
  const [actionBusy, setActionBusy] = useState(false);
  const [assign, setAssign] = useState({ indicator_id: "", member_ref: "", expires_at: "" });
  const [assignError, setAssignError] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);
  const createFileRef = useRef(null);
  const editFileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/admin/indicators").then((res) => setIndicators(res.data)).catch(() => setIndicators([]));
    api.get("/admin/indicator-requests").then((res) => setRequests(res.data)).catch(() => setRequests([]));
    api.get("/admin/indicator-access").then((res) => setAccessList(res.data)).catch(() => setAccessList([]));
  }, []);
  useEffect(load, [load]);

  const accessQuery = accessSearch.trim().toLowerCase();
  const visibleAccess = accessList
    ? accessList.filter((a) => !accessQuery || (a.member?.email || "").toLowerCase().includes(accessQuery))
    : null;

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    if (form.access_type === "coin" && !(Number(form.coin_price) > 0)) {
      setError("Coins price is required when access type is Coins");
      setLoading(false);
      return;
    }
    try {
      await api.post("/admin/indicators", { ...form, image_url: form.image_url || null, ...coinPayload(form) });
      toast.success("Indicator created");
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const toggleIndicator = async (indicator) => {
    try {
      await api.put(`/admin/indicators/${indicator.id}`, {
        name: indicator.name,
        description: indicator.description,
        full_description: indicator.full_description,
        image_url: indicator.image_url,
        tradingview_ref: indicator.tradingview_ref,
        tutorial_link: indicator.tutorial_link,
        status: indicator.status === "active" ? "inactive" : "active",
        access_type: indicator.access_type || "free",
        eligible_plans: indicator.eligible_plans || [],
        coin_price_minor: indicator.coin_price_minor ?? null,
        coin_duration_days: indicator.coin_duration_days ?? null,
      });
      toast.success(`Indicator ${indicator.status === "active" ? "unpublished" : "published"}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteIndicator = async (indicator) => {
    if (!window.confirm(`Delete indicator "${indicator.name}"? Claim and access history is preserved.`)) return;
    try {
      await api.delete(`/admin/indicators/${indicator.id}`);
      toast.success("Indicator deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveEditInd = async () => {
    setEditBusy(true);
    setEditError("");
    if (editInd.access_type === "coin" && !(Number(editInd.coin_price) > 0)) {
      setEditError("Coins price is required when access type is Coins");
      setEditBusy(false);
      return;
    }
    try {
      await api.put(`/admin/indicators/${editInd.id}`, {
        name: editInd.name,
        description: editInd.description,
        full_description: editInd.full_description,
        image_url: editInd.image_url || null,
        tradingview_ref: editInd.tradingview_ref,
        tutorial_link: editInd.tutorial_link || null,
        status: editInd.status,
        access_type: editInd.access_type || "free",
        eligible_plans: editInd.eligible_plans || [],
        ...coinPayload(editInd),
      });
      toast.success("Indicator updated");
      setEditInd(null);
      load();
    } catch (err) {
      setEditError(formatApiError(err));
    } finally {
      setEditBusy(false);
    }
  };

  const submitApprove = async () => {
    const isPurchase = approveModal.request.type === "purchase";
    if (!isPurchase && !approveModal.expiry_date) {
      toast.error("Expiry date is required");
      return;
    }
    setActionBusy(true);
    try {
      await api.post(`/admin/indicator-requests/${approveModal.request.id}/approve`, {
        start_date: approveModal.start_date || null,
        expiry_date: isPurchase ? null : new Date(approveModal.expiry_date).toISOString(),
        remark: approveModal.remark,
      });
      toast.success("Request approved — access activated");
      setApproveModal(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActionBusy(false);
    }
  };

  const submitReject = async () => {
    if (!rejectModal.reason.trim()) {
      toast.error("A rejection reason is required");
      return;
    }
    setActionBusy(true);
    try {
      await api.post(`/admin/indicator-requests/${rejectModal.request.id}/reject`, { reason: rejectModal.reason });
      toast.success("Request rejected");
      setRejectModal(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActionBusy(false);
    }
  };

  const openHistory = async (r) => {
    setHistoryModal({ member_id: r.member_id, member_name: r.member_name, data: null });
    try {
      const { data } = await api.get(`/admin/members/${r.member_id}/indicator-history`);
      setHistoryModal({ member_id: r.member_id, member_name: r.member_name, data });
    } catch (err) {
      toast.error(formatApiError(err));
      setHistoryModal(null);
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    setAssignError("");
    setAssignLoading(true);
    try {
      const { data } = await api.post(`/admin/indicators/${assign.indicator_id}/assign`, {
        member_ref: assign.member_ref,
        expires_at: assign.expires_at ? new Date(assign.expires_at).toISOString() : null,
      });
      toast.success(data.message);
      setAssign({ indicator_id: "", member_ref: "", expires_at: "" });
      load();
    } catch (err) {
      setAssignError(formatApiError(err));
    } finally {
      setAssignLoading(false);
    }
  };

  const revoke = async (access) => {
    try {
      await api.patch(`/admin/indicator-access/${access.id}/revoke`);
      toast.success("Access revoked");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const filteredRequests = (requests || []).filter((r) => matchesFilter(r, filter));
  const pendingCount = (requests || []).filter((r) => matchesFilter(r, "pending") || r.status === "renewal_pending").length;

  return (
    <div className="space-y-8" data-testid="admin-indicators-page">
      <div>
        <p className="eyebrow-label mb-2">Management</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">Indicator Management</h1>
        <p className="mt-2 text-sm text-[#64748B]">
          Publish indicators, review claims and renewals, and control member access with mandatory expiry dates.
        </p>
      </div>

      {/* ---------- Requests inbox ---------- */}
      <div className="card-lux overflow-hidden" data-testid="indicator-requests-section">
        <div className="px-6 pt-6 flex items-center justify-between flex-wrap gap-3">
          <p className="eyebrow-label">Indicator Requests {pendingCount > 0 && <span className="ml-2 text-[#D4AF37] font-mono">({pendingCount} awaiting)</span>}</p>
          <div className="flex gap-2 flex-wrap" data-testid="indicator-request-filters">
            {REQUEST_FILTERS.map(([key, label]) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-3 py-1.5 border transition-colors ${
                  filter === key ? "text-[#D4AF37] border-[#D4AF37]/50 bg-[#D4AF37]/10" : "text-[#64748B] border-[#222530] hover:border-[#D4AF37]/30"
                }`}
                data-testid={`request-filter-${key}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm" data-testid="indicator-requests-table">
            <thead>
              <tr className="border-b border-[#222530] text-left">
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Member</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Indicator</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden lg:table-cell">IB</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden md:table-cell">Requested</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Status</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#222530]">
              {requests === null ? (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-[#64748B]">Loading requests...</td></tr>
              ) : filteredRequests.length === 0 ? (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-[#64748B]" data-testid="indicator-requests-empty">No requests in this view.</td></tr>
              ) : (
                filteredRequests.map((r) => (
                  <tr key={r.id} className="hover:bg-[#0B0C10]/60 transition-colors" data-testid={`indicator-request-row-${r.id.slice(-6)}`}>
                    <td className="px-6 py-4">
                      <p className="text-white font-medium">{r.member_name}</p>
                      <p className="text-xs text-[#64748B] font-mono">{r.member_uid} · {r.email}</p>
                      <p className="text-[10px] text-[#64748B] font-mono">{r.registered_broker}{r.tradingview_username ? ` · TV: ${r.tradingview_username}` : ""}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-white">{r.indicator_name}</p>
                      {r.type === "second" && (
                        <span className="inline-block mt-1 text-[9px] font-mono tracking-widest uppercase text-[#D4AF37] border border-[#D4AF37]/40 rounded px-1.5 py-0.5" data-testid={`second-request-badge-${r.id.slice(-6)}`}>
                          Second Indicator Request
                        </span>
                      )}
                      {r.type === "renewal" && (
                        <span className="inline-block mt-1 text-[9px] font-mono tracking-widest uppercase text-[#94A3B8] border border-[#222530] rounded px-1.5 py-0.5">Renewal</span>
                      )}
                      {r.type === "purchase" && (
                        <span className="inline-block mt-1 text-[9px] font-mono tracking-widest uppercase text-emerald-400 border border-emerald-400/40 rounded px-1.5 py-0.5" data-testid={`purchase-request-badge-${r.id.slice(-6)}`}>
                          Coins Purchase — Paid
                        </span>
                      )}
                      {r.access_type === "subscription" && (
                        <span className="inline-block mt-1 ml-1 text-[9px] font-mono tracking-widest uppercase text-[#D4AF37] border border-[#D4AF37]/40 rounded px-1.5 py-0.5" data-testid={`sub-request-badge-${r.id.slice(-6)}`}>
                          {r.approval_mode === "auto" ? "AUTO APPROVED" : "SUBSCRIPTION"} · {r.subscription_plan || ""}
                        </span>
                      )}
                      {r.type === "second" && r.existing_indicator && (
                        <p className="text-[10px] text-[#64748B] mt-1">Holds: {r.existing_indicator}</p>
                      )}
                      {r.type === "second" && r.ib_remark && (
                        <p className="text-[10px] text-[#94A3B8] mt-0.5">IB remark: {r.ib_remark}</p>
                      )}
                    </td>
                    <td className="px-6 py-4 hidden lg:table-cell">
                      <p className="text-white text-xs">{r.ib_name || "—"}</p>
                      <p className="text-[10px] text-[#64748B] font-mono">{r.ib_uid || ""}</p>
                    </td>
                    <td className="px-6 py-4 text-[#94A3B8] hidden md:table-cell">{r.created_at ? fmtMYTDate(r.created_at) : "—"}</td>
                    <td className="px-6 py-4">
                      <span className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 border ${STATUS_STYLES[r.status]}`} data-testid={`request-status-${r.id.slice(-6)}`}>
                        {STATUS_LABELS[r.status]}
                      </span>
                      {r.status === "approved" && r.access_expires_at && (
                        <p className={`text-[10px] font-mono mt-1 ${r.access_status === "expired" ? "text-red-400" : "text-emerald-400"}`}>
                          {r.access_status === "expired" ? "Expired" : "Until"} {fmtMYTDate(r.access_expires_at)}
                        </p>
                      )}
                      {r.reject_reason && <p className="text-[10px] text-red-400 mt-1 max-w-[180px]">{r.reject_reason}</p>}
                    </td>
                    <td className="px-6 py-4 text-right whitespace-nowrap">
                      {["pending_admin", "ib_approved", "renewal_pending"].includes(r.status) && (
                        <>
                          <button
                            onClick={() => setApproveModal({ request: r, start_date: "", expiry_date: "", remark: "" })}
                            className="text-xs text-emerald-400 border border-emerald-400/30 hover:bg-emerald-400/10 rounded-lg px-2.5 py-1.5 transition-colors mr-2"
                            data-testid={`request-approve-${r.id.slice(-6)}`}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => setRejectModal({ request: r, reason: "" })}
                            className="text-xs text-red-400 border border-red-400/30 hover:bg-red-400/10 rounded-lg px-2.5 py-1.5 transition-colors mr-2"
                            data-testid={`request-reject-${r.id.slice(-6)}`}
                          >
                            Reject
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => openHistory(r)}
                        className="text-xs text-[#94A3B8] hover:text-white border border-[#222530] hover:border-[#D4AF37]/40 rounded-lg px-2.5 py-1.5 transition-colors"
                        data-testid={`request-history-${r.id.slice(-6)}`}
                      >
                        History
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ---------- Catalog ---------- */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-lux p-8">
          <p className="eyebrow-label mb-6">Add New Indicator</p>
          <form onSubmit={handleCreate} className="space-y-5">
            <div>
              <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Indicator Name</label>
              <input className="input-lux" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. TZB" required data-testid="indicator-name-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Short Description</label>
              <input className="input-lux" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="One-line summary" data-testid="indicator-description-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Full Description</label>
              <textarea className="input-lux min-h-[90px]" value={form.full_description} onChange={(e) => setForm((f) => ({ ...f, full_description: e.target.value }))} placeholder="Detailed description shown in the member library" data-testid="indicator-full-description-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Indicator Image</label>
              <div className="flex items-center gap-3">
                {form.image_url && <img src={form.image_url} alt="" className="h-11 w-11 rounded-lg object-cover border border-[#222530]" />}
                <input type="file" accept="image/*" ref={createFileRef} className="hidden" onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    const url = await uploadImage(file);
                    setForm((f) => ({ ...f, image_url: url }));
                    toast.success("Image uploaded");
                  } catch (err) { toast.error(formatApiError(err)); }
                }} data-testid="indicator-image-file-input" />
                <button type="button" onClick={() => createFileRef.current?.click()} className="btn-outline-gold px-4 py-2 text-xs flex items-center gap-2" data-testid="indicator-image-upload-button">
                  <ImagePlus className="h-3.5 w-3.5" /> {form.image_url ? "Change Image" : "Upload Image"}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">
                TradingView Reference <span className="text-[#64748B]">(optional, future use)</span>
              </label>
              <input className="input-lux font-mono" value={form.tradingview_ref} onChange={(e) => setForm((f) => ({ ...f, tradingview_ref: e.target.value }))} placeholder="e.g. PUB;abc123" data-testid="indicator-tradingview-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">
                Tutorial Link <span className="text-[#64748B]">(optional — shown to members after approval)</span>
              </label>
              <input className="input-lux font-mono" value={form.tutorial_link} onChange={(e) => setForm((f) => ({ ...f, tutorial_link: e.target.value }))} placeholder="https://t.me/..." data-testid="indicator-tutorial-link-input" />
            </div>
            <AccessConfig value={form} onChange={setForm} prefix="indicator" />
            <label className="flex items-center gap-3 text-sm text-[#94A3B8]">
              <input type="checkbox" checked={form.status === "active"} onChange={(e) => setForm((f) => ({ ...f, status: e.target.checked ? "active" : "inactive" }))} className="accent-[#D4AF37]" data-testid="indicator-publish-checkbox" />
              Publish immediately (visible in the member library)
            </label>
            {error && (
              <p className="text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="indicator-create-error">{error}</p>
            )}
            <button type="submit" disabled={loading} className="btn-gold px-8 py-3 text-sm disabled:opacity-60" data-testid="indicator-create-button">
              {loading ? "Creating..." : "Create Indicator"}
            </button>
          </form>
        </div>

        <div className="card-lux p-8">
          <p className="eyebrow-label mb-6">Assign Access to Member <span className="text-[#64748B] normal-case tracking-normal">(legacy direct grant)</span></p>
          <form onSubmit={handleAssign} className="space-y-5">
            <div>
              <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Indicator</label>
              <select className="input-lux" value={assign.indicator_id} onChange={(e) => setAssign((a) => ({ ...a, indicator_id: e.target.value }))} required data-testid="assign-indicator-select">
                <option value="">Select indicator...</option>
                {(indicators || []).filter((i) => i.status === "active").map((i) => (
                  <option key={i.id} value={i.id}>{i.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Member UID or Username</label>
              <input className="input-lux font-mono" value={assign.member_ref} onChange={(e) => setAssign((a) => ({ ...a, member_ref: e.target.value }))} placeholder="e.g. PM100001 or 00112233" required data-testid="assign-member-ref-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Expiry Date <span className="text-[#64748B]">(optional)</span></label>
              <input type="date" className="input-lux" value={assign.expires_at} onChange={(e) => setAssign((a) => ({ ...a, expires_at: e.target.value }))} data-testid="assign-expiry-input" />
            </div>
            {assignError && (
              <p className="text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="assign-error">{assignError}</p>
            )}
            <button type="submit" disabled={assignLoading} className="btn-gold px-8 py-3 text-sm disabled:opacity-60" data-testid="assign-submit-button">
              {assignLoading ? "Assigning..." : "Grant Access"}
            </button>
          </form>
        </div>
      </div>

      <div className="card-lux overflow-hidden">
        <div className="px-6 pt-6"><p className="eyebrow-label">Indicators</p></div>
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm" data-testid="admin-indicators-table">
            <thead>
              <tr className="border-b border-[#222530] text-left">
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Indicator</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden md:table-cell">TradingView Ref</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden lg:table-cell">Access</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Assigned</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Status</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#222530]">
              {indicators === null ? (
                <tr><td colSpan={5} className="px-6 py-10 text-center text-[#64748B]">Loading indicators...</td></tr>
              ) : indicators.length === 0 ? (
                <tr><td colSpan={5} className="px-6 py-10 text-center text-[#64748B]" data-testid="admin-indicators-empty">No indicators created yet.</td></tr>
              ) : (
                indicators.map((i) => (
                  <tr key={i.id} className="hover:bg-[#0B0C10]/60 transition-colors" data-testid={`admin-indicator-row-${i.name}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {i.image_url ? (
                          <img src={i.image_url} alt="" className="h-10 w-10 rounded-lg object-cover border border-[#222530]" />
                        ) : (
                          <div className="h-10 w-10 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/25 flex items-center justify-center">
                            <LineChart className="h-4 w-4 text-[#D4AF37]" />
                          </div>
                        )}
                        <div>
                          <p className="text-white font-medium">{i.name}</p>
                          <p className="text-xs text-[#64748B]">{i.description}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 hidden md:table-cell">
                      <span className="font-mono text-xs text-[#94A3B8]">{i.tradingview_ref || "—"}</span>
                      <span className="block text-[9px] font-mono tracking-widest uppercase text-[#64748B] mt-1">Manual / Integration Pending</span>
                    </td>
                    <td className="px-6 py-4 hidden lg:table-cell" data-testid={`admin-indicator-access-${i.name}`}>
                      {i.access_type === "coin" ? (
                        <span className="text-[10px] font-mono tracking-widest uppercase text-[#D4AF37] border border-[#D4AF37]/30 rounded-full px-2 py-0.5" data-testid={`admin-indicator-access-coin-${i.name}`}>COINS</span>
                      ) : (
                        <span className="text-[10px] font-mono tracking-widest uppercase text-emerald-400 border border-emerald-400/30 rounded-full px-2 py-0.5">FREE</span>
                      )}
                      {i.coin_price_minor != null && (
                        <span className="block mt-1 text-[10px] font-mono text-[#D4AF37]" data-testid={`admin-indicator-coin-price-${i.name}`}>
                          {(i.coin_price_minor / 100).toLocaleString("en-US")} PM{i.coin_duration_days ? ` · ${i.coin_duration_days}d` : " · Permanent"}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 font-mono text-white">{i.assigned_count}</td>
                    <td className="px-6 py-4">
                      <span className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 border ${
                        i.status === "active" ? "text-emerald-400 bg-emerald-400/10 border-emerald-400/30" : "text-[#64748B] border-[#222530]"
                      }`} data-testid={`admin-indicator-status-${i.name}`}>{i.status === "active" ? "Published" : "Unpublished"}</span>
                    </td>
                    <td className="px-6 py-4 text-right whitespace-nowrap">
                      <button
                        onClick={() => { setEditError(""); setEditInd({ id: i.id, name: i.name, description: i.description, full_description: i.full_description || "", image_url: i.image_url || "", tradingview_ref: i.tradingview_ref || "", tutorial_link: i.tutorial_link || "", status: i.status, access_type: i.access_type || "free", eligible_plans: i.eligible_plans || [], coin_price: i.coin_price_minor != null ? String(i.coin_price_minor / 100) : "", coin_duration_days: i.coin_duration_days != null ? String(i.coin_duration_days) : "" }); }}
                        className="text-xs text-[#94A3B8] hover:text-white border border-[#222530] hover:border-[#D4AF37]/40 rounded-lg px-2.5 py-1.5 transition-colors inline-flex items-center gap-1 mr-2"
                        data-testid={`admin-indicator-edit-${i.name}`}
                      >
                        <Pencil className="h-3 w-3" /> Edit
                      </button>
                      <button onClick={() => toggleIndicator(i)} className={`text-xs font-medium rounded-lg px-3 py-1.5 border transition-colors mr-2 ${
                        i.status === "active" ? "text-red-400 border-red-400/30 hover:bg-red-400/10" : "text-emerald-400 border-emerald-400/30 hover:bg-emerald-400/10"
                      }`} data-testid={`admin-indicator-toggle-${i.name}`}>
                        {i.status === "active" ? "Unpublish" : "Publish"}
                      </button>
                      <button onClick={() => deleteIndicator(i)} className="text-xs text-red-400 border border-red-400/30 hover:bg-red-400/10 rounded-lg px-2.5 py-1.5 transition-colors" data-testid={`admin-indicator-delete-${i.name}`}>
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card-lux overflow-hidden">
        <div className="px-6 pt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="eyebrow-label">Access Grants</p>
          <input
            type="text"
            value={accessSearch}
            onChange={(e) => setAccessSearch(e.target.value)}
            placeholder="Search by client email…"
            className="input-lux w-full sm:max-w-xs"
            data-testid="admin-access-search"
          />
        </div>
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm" data-testid="admin-access-table">
            <thead>
              <tr className="border-b border-[#222530] text-left">
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Member</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Indicator</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden sm:table-cell">Granted</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden md:table-cell">Expires</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Status</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#222530]">
              {accessList === null ? (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-[#64748B]">Loading grants...</td></tr>
              ) : accessList.length === 0 ? (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-[#64748B]" data-testid="admin-access-empty">No indicator access granted yet.</td></tr>
              ) : visibleAccess.length === 0 ? (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-[#64748B]" data-testid="admin-access-no-match">No grants match “{accessSearch}”.</td></tr>
              ) : (
                visibleAccess.map((a) => (
                  <tr key={a.id} className="hover:bg-[#0B0C10]/60 transition-colors" data-testid={`admin-access-row-${a.id.slice(-6)}`}>
                    <td className="px-6 py-4">
                      <p className="text-white font-medium">{a.member?.full_name || "Unknown"}</p>
                      <p className="text-xs text-[#64748B] font-mono">{a.member?.uid} · {a.member?.username}</p>
                      <p className="text-xs text-[#94A3B8] break-all" data-testid={`admin-access-email-${a.id.slice(-6)}`}>{a.member?.email || "—"}</p>
                      <p className="text-xs text-[#D4AF37] font-mono break-all mt-0.5" data-testid={`admin-access-tv-${a.id.slice(-6)}`}>TradingView: {a.tradingview_username || "Not recorded"}</p>
                    </td>
                    <td className="px-6 py-4 text-white">{a.indicator_name}</td>
                    <td className="px-6 py-4 text-[#94A3B8] hidden sm:table-cell">{a.granted_at ? fmtMYTDate(a.granted_at) : "—"}</td>
                    <td className="px-6 py-4 text-[#94A3B8] hidden md:table-cell">{a.expires_at ? fmtMYTDate(a.expires_at) : "—"}</td>
                    <td className="px-6 py-4">
                      <span className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 border ${
                        a.status === "active" ? "text-emerald-400 bg-emerald-400/10 border-emerald-400/30"
                        : a.status === "paused" ? "text-amber-400 bg-amber-400/10 border-amber-400/30"
                        : "text-red-400 bg-red-400/10 border-red-400/30"
                      }`}>{a.status}</span>
                      {a.source === "subscription" && (
                        <p className="text-[9px] font-mono tracking-widest uppercase text-[#D4AF37] mt-1">Subscription · {a.plan_name || "—"}</p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {a.status === "active" ? (
                        <button onClick={() => revoke(a)} className="text-xs text-red-400 border border-red-400/30 hover:bg-red-400/10 rounded-lg px-2.5 py-1.5 transition-colors" data-testid={`admin-access-revoke-${a.id.slice(-6)}`}>
                          Revoke
                        </button>
                      ) : (
                        <span className="text-xs text-[#64748B]">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ---------- Edit indicator modal ---------- */}
      {editInd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="edit-indicator-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setEditInd(null)} />
          <div className="card-lux relative w-full max-w-md p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
            <p className="eyebrow-label mb-2">Edit Indicator</p>
            <h3 className="font-heading text-lg font-semibold text-white mb-6">Indicator Details</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Name</label>
                <input className="input-lux" value={editInd.name} onChange={(e) => setEditInd((m) => ({ ...m, name: e.target.value }))} data-testid="edit-indicator-name-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Short Description</label>
                <input className="input-lux" value={editInd.description} onChange={(e) => setEditInd((m) => ({ ...m, description: e.target.value }))} data-testid="edit-indicator-description-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Full Description</label>
                <textarea className="input-lux min-h-[80px]" value={editInd.full_description} onChange={(e) => setEditInd((m) => ({ ...m, full_description: e.target.value }))} data-testid="edit-indicator-full-description-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Indicator Image</label>
                <div className="flex items-center gap-3">
                  {editInd.image_url && <img src={editInd.image_url} alt="" className="h-11 w-11 rounded-lg object-cover border border-[#222530]" />}
                  <input type="file" accept="image/*" ref={editFileRef} className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const url = await uploadImage(file);
                      setEditInd((m) => ({ ...m, image_url: url }));
                      toast.success("Image uploaded");
                    } catch (err) { toast.error(formatApiError(err)); }
                  }} data-testid="edit-indicator-image-file-input" />
                  <button type="button" onClick={() => editFileRef.current?.click()} className="btn-outline-gold px-4 py-2 text-xs flex items-center gap-2" data-testid="edit-indicator-image-upload-button">
                    <ImagePlus className="h-3.5 w-3.5" /> {editInd.image_url ? "Change Image" : "Upload Image"}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">
                  TradingView Reference <span className="text-[#64748B]">(Manual / Integration Pending)</span>
                </label>
                <input className="input-lux font-mono" value={editInd.tradingview_ref} onChange={(e) => setEditInd((m) => ({ ...m, tradingview_ref: e.target.value }))} data-testid="edit-indicator-tradingview-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">
                  Tutorial Link <span className="text-[#64748B]">(optional — shown to members after approval)</span>
                </label>
                <input className="input-lux font-mono" value={editInd.tutorial_link || ""} onChange={(e) => setEditInd((m) => ({ ...m, tutorial_link: e.target.value }))} placeholder="https://t.me/..." data-testid="edit-indicator-tutorial-link-input" />
              </div>
              <AccessConfig value={editInd} onChange={setEditInd} prefix="edit-indicator" />
              {editError && (
                <p className="text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="edit-indicator-error">{editError}</p>
              )}
              <div className="flex gap-3 pt-2">
                <button onClick={saveEditInd} disabled={editBusy} className="btn-gold flex-1 py-2.5 text-sm disabled:opacity-60" data-testid="edit-indicator-save-button">
                  {editBusy ? "Saving..." : "Save"}
                </button>
                <button onClick={() => setEditInd(null)} className="btn-outline-gold px-5 py-2.5 text-sm" data-testid="edit-indicator-cancel-button">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Approve modal ---------- */}
      {approveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="approve-request-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setApproveModal(null)} />
          <div className="card-lux relative w-full max-w-md p-6 sm:p-8">
            <p className="eyebrow-label mb-2">Approve Request</p>
            <h3 className="font-heading text-lg font-semibold text-white" data-testid="approve-request-title">
              {approveModal.request.indicator_name} — {approveModal.request.member_name}
            </h3>
            <p className="text-xs text-[#64748B] font-mono mt-1 mb-6">{approveModal.request.member_uid}{approveModal.request.type === "second" ? " · Second indicator (IB approved)" : approveModal.request.type === "renewal" ? " · Renewal" : approveModal.request.type === "purchase" ? " · Coins Purchase (paid)" : ""}</p>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Access Start Date <span className="text-[#64748B]">(optional)</span></label>
                <input type="date" className="input-lux" value={approveModal.start_date} onChange={(e) => setApproveModal((m) => ({ ...m, start_date: e.target.value }))} data-testid="approve-start-date-input" />
              </div>
              {approveModal.request.type === "purchase" ? (
                <p className="text-[11px] text-[#D4AF37] border border-[#D4AF37]/25 bg-[#D4AF37]/[0.05] rounded-lg px-4 py-3" data-testid="approve-auto-expiry-note">
                  Coins purchase — expiry is set automatically from the product's configured duration, starting at the access start date (permanent when no duration is configured). No manual expiry needed.
                </p>
              ) : (
                <div>
                  <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Indicator Expiry Date <span className="text-red-400">*</span></label>
                  <input type="date" className="input-lux" value={approveModal.expiry_date} onChange={(e) => setApproveModal((m) => ({ ...m, expiry_date: e.target.value }))} required data-testid="approve-expiry-date-input" />
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Admin Remark / Instruction</label>
                <textarea className="input-lux min-h-[70px]" value={approveModal.remark} onChange={(e) => setApproveModal((m) => ({ ...m, remark: e.target.value }))} placeholder="Optional instructions for the member" data-testid="approve-remark-input" />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={submitApprove} disabled={actionBusy} className="btn-gold flex-1 py-2.5 text-sm disabled:opacity-60" data-testid="approve-confirm-button">
                  {actionBusy ? "Approving..." : "Approve & Activate"}
                </button>
                <button onClick={() => setApproveModal(null)} className="btn-outline-gold px-5 py-2.5 text-sm" data-testid="approve-cancel-button">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Reject modal ---------- */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="reject-request-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setRejectModal(null)} />
          <div className="card-lux relative w-full max-w-md p-6 sm:p-8">
            <p className="eyebrow-label mb-2">Reject Request</p>
            <h3 className="font-heading text-lg font-semibold text-white mb-6">
              {rejectModal.request.indicator_name} — {rejectModal.request.member_name}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Rejection Reason <span className="text-red-400">*</span></label>
                <textarea className="input-lux min-h-[80px]" value={rejectModal.reason} onChange={(e) => setRejectModal((m) => ({ ...m, reason: e.target.value }))} placeholder="Shared with the member" data-testid="reject-reason-input" />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={submitReject} disabled={actionBusy} className="flex-1 py-2.5 text-sm rounded-lg bg-red-500/90 hover:bg-red-500 text-white font-medium transition-colors disabled:opacity-60" data-testid="reject-confirm-button">
                  {actionBusy ? "Rejecting..." : "Confirm Rejection"}
                </button>
                <button onClick={() => setRejectModal(null)} className="btn-outline-gold px-5 py-2.5 text-sm" data-testid="reject-cancel-button">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------- History modal ---------- */}
      {historyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="indicator-history-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setHistoryModal(null)} />
          <div className="card-lux relative w-full max-w-xl p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
            <p className="eyebrow-label mb-2">Indicator Access History</p>
            <h3 className="font-heading text-lg font-semibold text-white mb-6" data-testid="history-member-name">{historyModal.member_name}</h3>
            {!historyModal.data ? (
              <p className="text-sm text-[#64748B]">Loading history...</p>
            ) : (
              <>
                <p className="eyebrow-label mb-3">Access Records</p>
                {historyModal.data.access.length === 0 ? (
                  <p className="text-xs text-[#64748B] mb-6">No access records.</p>
                ) : (
                  <div className="space-y-2 mb-6" data-testid="history-access-list">
                    {historyModal.data.access.map((a) => (
                      <div key={a.id} className="border border-[#222530] rounded-lg px-4 py-3 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="text-white font-medium">{a.indicator_name}</span>
                          <span className={`font-mono uppercase ${a.status === "active" ? "text-emerald-400" : "text-red-400"}`}>{a.status}</span>
                        </div>
                        <p className="text-[#64748B] mt-1">
                          Granted {a.granted_at ? fmtMYTDate(a.granted_at) : "—"} · Expires {a.expires_at ? fmtMYTDate(a.expires_at) : "—"}
                        </p>
                        {a.previous_expiries?.length > 0 && (
                          <p className="text-[#64748B] mt-1">Previous expiries: {a.previous_expiries.filter(Boolean).map((d) => fmtMYTDate(d)).join(", ")}</p>
                        )}
                        {a.history?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {a.history.map((h, i) => (
                              <p key={i} className="text-[10px] text-[#94A3B8] font-mono">
                                {h.at ? fmtMYT(h.at) : ""} — {h.event}{h.remark ? ` (${h.remark})` : ""}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <p className="eyebrow-label mb-3">Requests & Decisions</p>
                {historyModal.data.requests.length === 0 ? (
                  <p className="text-xs text-[#64748B]">No requests.</p>
                ) : (
                  <div className="space-y-2" data-testid="history-requests-list">
                    {historyModal.data.requests.map((r) => (
                      <div key={r.id} className="border border-[#222530] rounded-lg px-4 py-3 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="text-white font-medium">{r.indicator_name} <span className="text-[#64748B]">({r.type})</span></span>
                          <span className="font-mono uppercase text-[#D4AF37]">{STATUS_LABELS[r.status] || r.status}</span>
                        </div>
                        <p className="text-[#64748B] mt-1">Requested {r.created_at ? fmtMYTDate(r.created_at) : "—"}</p>
                        {r.history?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {r.history.map((h, i) => (
                              <p key={i} className="text-[10px] text-[#94A3B8] font-mono">
                                {h.at ? fmtMYT(h.at) : ""} — {h.event}{h.remark ? ` (${h.remark})` : ""}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            <button onClick={() => setHistoryModal(null)} className="btn-outline-gold w-full py-2.5 text-sm mt-6" data-testid="history-close-button">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
